<script>
    import {updatePlanPoi, deletePlanPoi} from '@js/actions/poiActions';
    import {updateUser} from '@js/actions/userActions';
    import {showProductModal} from '@js/actions/basketActions';

    import {userStore} from '@js/stores/userStore';

    import {poiStore} from '@js/stores/poiStore';

    import I18n from '@js/modules/translations';
    import AnalyticsService from '@js/modules/analyticsService';

    import Notification from '@js/components/layouts/notification';
    import {PLAN_SORTS, planPoiState} from '@js/modules/plan/planPois';

    import Autocompletion from '@js/components/search/Autocompletion.svelte';

    import PlanMap from '@js/components/maps/PlanMap.svelte';
    import PlanModal from '@js/components/plan/PlanModal.svelte';
    import PlanList from '@js/components/plan/list/PlanList.svelte';
    import PlanFilters from '@js/components/plan/filter/PlanFilters.svelte';

    const PLAN_SORT_KEY = 'plan-list-sort';

    const {
        allFilters,
        placesCount: initialPlacesCount = 0,
        planReminders: initialPlanReminders = false
    } = $props();

    // All the user's places, whatever the filters: the free limit counts them
    const _initialPlacesCount = () => initialPlacesCount;
    let placesCount = $state(_initialPlacesCount());

    const _initialPlanReminders = () => initialPlanReminders;
    let planReminders = $state(_initialPlanReminders());

    // The same option as in the profile: the reminder emails of the planned places
    const _handleRemindersChange = (enabled) => {
        planReminders = enabled;

        // Not saved: the switch shows the option as it still is
        updateUser(window.currentUserId, {plan_reminders: enabled})
            .then((response) => {
                if (!response || response.errors) {
                    planReminders = !enabled;
                    Notification.error(I18n.t('js.helpers.errors.server'));
                }
            });
    };

    let isModalOpen = $state(false);
    let planPoiCoordinates = $state(null);
    let currentPlanPoi = $state(null);
    let currentPlanRide = $state(null);

    // The chosen sort survives a visit; storage can throw (private mode, in-app browsers)
    const _readSort = () => {
        try {
            return window.localStorage.getItem(PLAN_SORT_KEY);
        } catch {
            return null;
        }
    };

    let listSort = $state(PLAN_SORTS.includes(_readSort()) ? _readSort() : 'date');

    const overdueCount = $derived($poiStore.planPois.filter((planPoi) => planPoiState(planPoi) === 'overdue').length);

    const onSearchSubmit = (selectedLocation) => {
        poiStore.mapLocation = selectedLocation;
    };

    const _handleSortChange = (sortKey) => {
        listSort = sortKey;

        try {
            window.localStorage.setItem(PLAN_SORT_KEY, sortKey);
        } catch {
            // the sort is only kept for this visit
        }
    };

    // Over the free limit, the click opens the Explorer offer instead of a form the server would refuse
    // (same tiering as the ride creation: modal twice per session, then a toast)
    const _isOverPlacesLimit = () => {
        const limit = userStore.featureFlags?.poi_plan_max_number_count;

        return userStore.isConnected && limit != null && placesCount >= Number(limit);
    };

    const _handlePoiAdded = (coordinates) => {
        if (_isOverPlacesLimit()) {
            showProductModal('plan_poi_max_count');

            return;
        }

        planPoiCoordinates = coordinates;
        currentPlanPoi = null;
        currentPlanRide = null;

        isModalOpen = true;

        AnalyticsService.trackPlanPoiFormOpened(!userStore.isConnected);
    };

    const _handlePoiMoved = (coordinates, planPoi) => {
        return updatePlanPoi(planPoi.id, {
            poi: {
                latitude: coordinates.lat,
                longitude: coordinates.lng
            }
        }, false)
            .then((response) => {
                if (!response.errors) {
                    poiStore.planPois = poiStore.planPois.replaceOrAdd('id', response.poi);
                    poiStore.map.fetchPlanPOIs(poiStore.selectedFilters);

                    return response;
                }
            });
    };

    const _handlePoiRemoved = (removedPanPoi) => {
        return deletePlanPoi(removedPanPoi.id)
            .then((response) => {
                if (!response.errors) {
                    placesCount = Math.max(0, placesCount - 1);
                    poiStore.planPois = poiStore.planPois.filter((planPoi) => planPoi.id !== removedPanPoi.id);
                    poiStore.map.fetchPlanPOIs(poiStore.selectedFilters);

                    isModalOpen = false;

                    return response;
                }
            });
    };

    const _handlePoiEdit = (planPoi) => {
        planPoiCoordinates = {
            lat: planPoi.coordinates[0],
            lng: planPoi.coordinates[1]
        };
        currentPlanPoi = planPoi;
        currentPlanRide = planPoi.associatedRides[0];

        isModalOpen = true;
    };

    // Same confirmation as a right click on the map
    const _handlePoiDelete = (planPoi) => {
        poiStore.map?.confirmPoiRemoval(planPoi);
    };

    const _handleShowOnMap = (planPoi) => {
        document.getElementById('plan-rides')?.scrollIntoView({behavior: 'smooth', block: 'center'});

        poiStore.map?.centerPlanPoiMarker(planPoi.id, true);
    };

    // The ride picker (a second map) loads on demand, never with the page
    let RidePicker = $state(null);
    let pickerPlanPoi = $state(null);

    const _handlePoiAssociate = (planPoi) => {
        import('@js/components/plan/PlanRidePicker.svelte')
            .then((module) => {
                RidePicker = module.default;
                pickerPlanPoi = planPoi;
            })
            .catch(() => Notification.error(I18n.t('js.helpers.errors.server')));
    };

    const _handlePickerClose = () => {
        pickerPlanPoi = null;
    };

    const _handleRideAssociated = () => {
        pickerPlanPoi = null;

        poiStore.map?.fetchPlanPOIs(poiStore.selectedFilters);
    };
</script>

<Autocompletion id="global-search"
                placeholder={I18n.t('js.ride.plan.index.search')}
                searchFeatures={['location']}
                useFirstQueryOnEnter={false}
                onSubmit={onSearchSubmit}/>

<PlanFilters allFilters={allFilters}
             hasPlaces={placesCount > 0}
             overdueCount={overdueCount}/>

<PlanMap mapId="plan-rides"
         onPoiAdded={_handlePoiAdded}
         onPoiMoved={_handlePoiMoved}
         onPoiEdit={_handlePoiEdit}
         onPoiRemoved={_handlePoiRemoved}
         onPoiAssociate={_handlePoiAssociate}/>

<p class="-mt-2 text-xs text-gray-600 text-center dark:text-gray-300">
    <span class="hidden pointer-fine:inline">{I18n.t('js.ride.plan.index.hints.desktop')}</span>
    <span class="pointer-fine:hidden">{I18n.t('js.ride.plan.index.hints.touch')}</span>
</p>

<PlanList sort={listSort}
          reminders={userStore.isConnected ? planReminders : null}
          onRemindersChange={_handleRemindersChange}
          onSortChange={_handleSortChange}
          onShowOnMap={_handleShowOnMap}
          onEdit={_handlePoiEdit}
          onDelete={_handlePoiDelete}/>

{#if RidePicker && pickerPlanPoi}
    <RidePicker planPoi={pickerPlanPoi}
                onAssociated={_handleRideAssociated}
                onClose={_handlePickerClose}/>
{/if}

<PlanModal bind:isModalOpen={isModalOpen}
           planPoiCoordinates={planPoiCoordinates}
           planPoi={currentPlanPoi}
           planRide={currentPlanRide}
           onDelete={_handlePoiDelete}
           onCreated={() => placesCount++}/>
